import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { FileDashed, Lightning, Columns, Bug, ArrowLeft, Check, Plugs } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Same icon set as backend template.icon values
const TEMPLATE_ICON = {
  FileBlank: FileDashed,
  Lightning: Lightning,
  Columns: Columns,
  Bug: Bug,
};

const ACCENT = {
  zinc:    { ring: 'ring-zinc-900',    iconBg: 'bg-zinc-100',    iconText: 'text-zinc-700',   dot: 'bg-zinc-400' },
  indigo:  { ring: 'ring-indigo-500',  iconBg: 'bg-indigo-50',   iconText: 'text-indigo-600', dot: 'bg-indigo-500' },
  emerald: { ring: 'ring-emerald-500', iconBg: 'bg-emerald-50',  iconText: 'text-emerald-600',dot: 'bg-emerald-500' },
  rose:    { ring: 'ring-rose-500',    iconBg: 'bg-rose-50',     iconText: 'text-rose-600',   dot: 'bg-rose-500' },
};

// Count how many status/field chips are hidden behind the "+N more" label.
function selected_more_count(tpl) {
  const shownStatuses = Math.min(tpl.custom_statuses?.length || 0, 3);
  const shownFields = Math.min(tpl.custom_fields?.length || 0, 3);
  const total = (tpl.custom_statuses?.length || 0) + (tpl.custom_fields?.length || 0);
  return Math.max(0, total - shownStatuses - shownFields);
}

export default function CreateProjectDialog({ open, onClose, onCreated, onImportJira }) {
  const { getOrgHeaders, API } = useAuth();
  const [step, setStep] = useState('template'); // 'template' | 'details'
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingTpls, setLoadingTpls] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep('template'); setSelected(null);
        setName(''); setKey(''); setDescription(''); setClientId('');
      }, 150);
    }
  }, [open]);

  // Fetch templates + clients on open
  useEffect(() => {
    if (!open) return;
    setLoadingTpls(true);
    axios.get(`${API}/projects/templates`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setTemplates(r.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTpls(false));
    axios.get(`${API}/clients`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setClients(r.data || []))
      .catch(() => setClients([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [submitError, setSubmitError] = useState('');

  // Reset error when user edits inputs so the banner doesn't linger.
  useEffect(() => { if (submitError) setSubmitError(''); }, [name, key, description]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePickTemplate = (tpl) => {
    setSelected(tpl);
    setStep('details');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !key.trim()) return;
    // Frontend mirror of the backend constraint — 6-8 alphanumeric chars.
    // Surface the violation inline so the user sees it before the network
    // round-trip and doesn't think the form is frozen.
    const cleanKey = key.trim().toUpperCase();
    if (!/^[A-Z0-9]{6,8}$/.test(cleanKey)) {
      const msg = 'Project key must be 6 to 8 alphanumeric characters (A-Z, 0-9).';
      setSubmitError(msg);
      try { toast.error(msg); } catch { /* ignore */ }
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(`${API}/projects`, {
        name: name.trim(),
        key: cleanKey,
        description,
        template: selected?.id || 'blank',
        client_id: clientId || null,
      }, { withCredentials: true, headers: getOrgHeaders() });
      onCreated();
    } catch (err) {
      // The backend returns helpful 409 messages for duplicate name / key.
      // Previously these were swallowed by a bare `console.error`, so the
      // dialog just appeared frozen. Surface them inline + as a toast so
      // users immediately know why the action failed.
      const msg = err?.response?.data?.detail || 'Failed to create project. Please try again.';
      setSubmitError(msg);
      try { toast.error(msg); } catch { /* toast may not be in scope in older callers */ }
      console.error('[create-project]', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={step === 'template' ? 'sm:max-w-2xl' : 'sm:max-w-md'} data-testid="create-project-dialog">
        {step === 'template' && (
          <>
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-lg font-bold text-zinc-900 tracking-tight">Choose a template</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">Pre-configured statuses and custom fields to match how your team works. You can change everything later.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-3" data-testid="template-grid">
              {loadingTpls && <div className="col-span-2 text-center text-sm text-zinc-400 py-8">Loading templates…</div>}
              {!loadingTpls && templates.map(tpl => {
                const Icon = TEMPLATE_ICON[tpl.icon] || FileDashed;
                const accent = ACCENT[tpl.accent] || ACCENT.zinc;
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => handlePickTemplate(tpl)}
                    data-testid={`template-${tpl.id}`}
                    className={`group relative text-left p-4 rounded-xl border border-zinc-200 bg-white hover:border-zinc-900 hover:shadow-sm transition-all duration-150`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accent.iconBg} ${accent.iconText}`}>
                      <Icon size={18} weight="duotone" />
                    </div>
                    <div className="font-['Outfit'] text-[15px] font-bold text-zinc-900 leading-tight">{tpl.name}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{tpl.tagline}</div>
                    <p className="text-[12px] text-zinc-600 mt-2 leading-snug">{tpl.description}</p>
                    {(tpl.custom_statuses?.length > 0 || tpl.custom_fields?.length > 0) && (
                      <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-wrap gap-1.5">
                        {tpl.custom_statuses.slice(0, 3).map(s => (
                          <span key={s.id} className="inline-flex items-center gap-1 text-[10px] text-zinc-600 bg-zinc-50 px-1.5 py-0.5 rounded">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
                            {s.label}
                          </span>
                        ))}
                        {tpl.custom_fields.slice(0, 3).map((f, i) => (
                          <span key={i} className="text-[10px] text-zinc-500 bg-zinc-50 px-1.5 py-0.5 rounded">{f.name}</span>
                        ))}
                        {selected_more_count(tpl) > 0 && (
                          <span className="text-[10px] text-zinc-400">+{selected_more_count(tpl)} more</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {onImportJira && (
              <button
                type="button"
                onClick={() => { onClose(); setTimeout(() => onImportJira(), 160); }}
                data-testid="create-project-import-jira"
                className="mt-3 w-full flex items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50/60 hover:bg-blue-50 hover:border-blue-300 transition-colors px-4 py-3 text-left"
              >
                <span className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                    <Plugs size={18} weight="duotone" />
                  </span>
                  <span>
                    <span className="block text-[13px] font-bold text-zinc-900">Migrating from Jira?</span>
                    <span className="block text-[11px] text-zinc-500">Import an existing Jira project — issues, comments & users come with it.</span>
                  </span>
                </span>
                <span className="text-[12px] font-semibold text-blue-700 whitespace-nowrap">Import from Jira →</span>
              </button>
            )}
          </>
        )}

        {step === 'details' && selected && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 text-[11px] text-zinc-400 uppercase tracking-[0.2em] font-medium mb-1">
                <button type="button" onClick={() => setStep('template')} data-testid="back-to-templates" className="flex items-center gap-1 hover:text-zinc-700 transition-colors">
                  <ArrowLeft size={12} /> Back
                </button>
                <span>·</span>
                <span>{selected.name} template</span>
              </div>
              <DialogTitle className="font-['Outfit'] text-lg font-bold text-zinc-900 tracking-tight">Project details</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">{selected.description}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium">Project Name</Label>
                <Input data-testid="create-project-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Mobile App Redesign" required autoFocus />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium">Project Key</Label>
                <Input
                  data-testid="create-project-key"
                  value={key}
                  // Alphanumeric uppercase, 6-8 chars total. We strip
                  // anything invalid as the user types (no flashing error
                  // toasts for keystrokes) and rely on submit-time
                  // validation to enforce the 6-char minimum.
                  onChange={e => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                  placeholder="e.g., WORDPR1"
                  minLength={6}
                  maxLength={8}
                  required
                />
                <p className="text-[10px] text-zinc-400">6–8 alphanumeric characters · used for task IDs like WORDPR1-1, WORDPR1-2</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium">Description</Label>
                <Textarea data-testid="create-project-description" value={description} onChange={e => setDescription(e.target.value)} placeholder="Briefly describe the project..." rows={2} className="resize-none" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium">Client <span className="normal-case tracking-normal text-zinc-300">(optional)</span></Label>
                <select
                  data-testid="create-project-client"
                  value={clientId}
                  onChange={e => setClientId(e.target.value)}
                  className="w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                >
                  <option value="">No client — internal project</option>
                  {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
                <p className="text-[10px] text-zinc-400">The client's logo &amp; colour will brand this board. Manage clients under <span className="font-medium">Clients</span>.</p>
              </div>
              {(selected.custom_statuses?.length > 0 || selected.custom_fields?.length > 0) && (
                <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-3" data-testid="template-preview">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">
                    <Check size={11} weight="bold" /> You'll get
                  </div>
                  <div className="space-y-1.5 text-[12px] text-zinc-700">
                    {selected.custom_statuses?.length > 0 && (
                      <div>
                        <span className="text-zinc-500">Extra statuses: </span>
                        {selected.custom_statuses.map(s => s.label).join(' · ')}
                      </div>
                    )}
                    {selected.custom_fields?.length > 0 && (
                      <div>
                        <span className="text-zinc-500">Custom fields: </span>
                        {selected.custom_fields.map(f => f.name).join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <Button type="submit" data-testid="submit-create-project" disabled={submitting} className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200">
                {submitting ? 'Creating...' : 'Create Project'}
              </Button>
              {submitError && (
                <div
                  data-testid="create-project-error"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
                  role="alert"
                >
                  {submitError}
                </div>
              )}
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
